import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Sparkles, MapPin } from 'lucide-react';
import BottomNavigation from '../components/BottomNavigation';
import LanguageToggle from '../components/LanguageToggle';
import { getHolidays, AVAILABLE_HOLIDAY_YEARS } from '../data/holidays';

// Find-2-Day visual classification (not an official legal colour scheme):
//   🟡 national · 🟢 andalusia · 🔴 local municipality
const SCOPE_LABELS = {
  national:  { key: 'holidays.scope.national',  fallback: 'National',   dot: 'bg-yellow-400', cls: 'bg-yellow-100 text-yellow-800 ring-yellow-200' },
  andalusia: { key: 'holidays.scope.andalusia', fallback: 'Andalucía',  dot: 'bg-green-500',  cls: 'bg-green-100 text-green-700 ring-green-200' },
  local:     { key: 'holidays.scope.local',     fallback: 'Local',      dot: 'bg-red-500',    cls: 'bg-red-100 text-red-700 ring-red-200' },
};

const LOCALE_MAP = { en: 'en-GB', es: 'es-ES', de: 'de-DE', fr: 'fr-FR', nl: 'nl-NL', ar: 'ar-ES' };

const Holidays = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = LOCALE_MAP[i18n.language] || 'en-GB';
  const [townFilter, setTownFilter] = useState('all');
  const defaultYear = new Date().getFullYear();
  const [year, setYear] = useState(AVAILABLE_HOLIDAY_YEARS.includes(defaultYear) ? defaultYear : AVAILABLE_HOLIDAY_YEARS[0]);

  const holidaysData = useMemo(() => getHolidays(year), [year]);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  // Distinct towns present in the selected year's local data.
  const towns = useMemo(
    () => [...new Set(holidaysData.filter((h) => h.scope === 'local' && h.town).map((h) => h.town))]
      .sort((a, b) => a.localeCompare(b)),
    [holidaysData]
  );
  const hasLocal = towns.length > 0;

  // Reset town filter when the year changes (avoid a stale town not in the new year).
  React.useEffect(() => { setTownFilter('all'); }, [year]);

  const enriched = useMemo(() => {
    return holidaysData
      .filter((h) => {
        if (h.scope !== 'local') return true;
        return townFilter === 'all' || h.town === townFilter;
      })
      .map((h) => {
        const d = new Date(h.date + 'T00:00:00');
        return {
          ...h,
          dateObj: d,
          monthNum: d.getMonth(),
          monthKey: d.toLocaleDateString('en-US', { month: 'long' }).toLowerCase(),
          daysAway: Math.round((d - today) / (1000 * 60 * 60 * 24)),
        };
      })
      .filter((h) => h.daysAway >= 0)  // display-only: hide holidays already past (keep today + future)
      .sort((a, b) => a.dateObj - b.dateObj);
  }, [today, townFilter, holidaysData]);

  const next = enriched.find((h) => h.daysAway >= 0);

  const byMonth = useMemo(() => {
    const groups = new Map();
    enriched.forEach((h) => {
      if (!groups.has(h.monthNum)) groups.set(h.monthNum, { monthKey: h.monthKey, items: [] });
      groups.get(h.monthNum).items.push(h);
    });
    return [...groups.entries()].sort((a, b) => a[0] - b[0]).map(([, v]) => v);
  }, [enriched]);

  const formatDate = (d) => d.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });
  const holidayName = (h) => t(h.nameKey, h.name || h.nameKey);

  return (
    <div className="min-h-screen bg-gray-50 pb-24" data-testid="holidays-page">
      <header className="bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-lg">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-white/20" data-testid="holidays-back-btn">
              <ArrowLeft className="h-5 w-5 rtl-flip" />
            </button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Calendar className="h-5 w-5" /> {t('holidays.title', 'Holidays')}
              </h1>
              <p className="text-xs text-purple-100">{t('holidays.subtitleShort', 'Spain & Andalucía')} · {year}</p>
            </div>
          </div>
          <LanguageToggle />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* Year selector */}
        <div className="flex items-center gap-2 mb-4" data-testid="holidays-year-selector">
          {AVAILABLE_HOLIDAY_YEARS.map((y) => (
            <button
              key={y}
              onClick={() => setYear(y)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${year === y ? 'bg-purple-600 text-white' : 'bg-white text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50'}`}
              data-testid={`holidays-year-${y}`}
            >
              {y}
            </button>
          ))}
        </div>
        {/* Colour legend */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4 text-xs" data-testid="holidays-legend">
          {Object.entries(SCOPE_LABELS).map(([scope, s]) => (
            <span key={scope} className="inline-flex items-center gap-1.5">
              <span className={`w-3 h-3 rounded-full ${s.dot}`} /> {t(s.key, s.fallback)}
            </span>
          ))}
        </div>

        {/* Municipality filter for local (🔴) holidays */}
        {hasLocal && (
          <div className="flex items-center gap-2 mb-5">
            <MapPin className="h-4 w-4 text-gray-400 shrink-0" />
            <select
              value={townFilter}
              onChange={(e) => setTownFilter(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 max-w-full"
              data-testid="holidays-town-filter"
            >
              <option value="all">{t('holidays.allTowns', 'All municipalities (local holidays)')}</option>
              {towns.map((tn) => (
                <option key={tn} value={tn}>{tn}</option>
              ))}
            </select>
          </div>
        )}

        {/* Local holidays not yet officially published for this year */}
        {!hasLocal && (
          <div className="flex items-start gap-2 mb-5 p-3 rounded-lg bg-amber-50 ring-1 ring-amber-200 text-amber-800 text-xs" data-testid="holidays-local-pending">
            <MapPin className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{t('holidays.localPending', 'Local municipality holidays for {{year}} will be added when officially published by the Junta de Andalucía (BOJA).', { year })}</span>
          </div>
        )}

        {next && (
          <div className="bg-gradient-to-br from-purple-600 to-purple-700 text-white rounded-2xl p-5 mb-6 shadow-lg" data-testid="holidays-next-card">
            <div className="flex items-center gap-2 text-purple-100 text-xs uppercase tracking-wider font-semibold">
              <Sparkles className="h-3.5 w-3.5" /> {t('holidays.nextHoliday', 'Next holiday')}
            </div>
            <h2 className="text-2xl font-bold mt-1">
              {holidayName(next)}
              {next.scope === 'local' && next.town ? ` — ${next.town}` : ''}
            </h2>
            <p className="text-purple-100 mt-1 text-sm">{formatDate(next.dateObj)}</p>
            <p className="mt-3 text-3xl font-extrabold">
              {next.daysAway === 0
                ? t('holidays.today', 'Today')
                : next.daysAway === 1
                ? t('holidays.tomorrow', 'Tomorrow')
                : t('holidays.inNDays', 'in {{count}} days', { count: next.daysAway })}
            </p>
          </div>
        )}

        <div className="space-y-6">
          {byMonth.map(({ monthKey, items }) => (
            <section key={monthKey} data-testid={`holidays-month-${monthKey}`}>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
                {t(`holidays.month.${monthKey}`, monthKey)}
              </h3>
              <div className="bg-white rounded-xl shadow-sm divide-y divide-gray-100">
                {items.map((h) => {
                  const s = SCOPE_LABELS[h.scope] || SCOPE_LABELS.national;
                  const isPast = h.daysAway < 0;
                  const isLocal = h.scope === 'local';
                  return (
                    <div
                      key={`${h.date}-${h.town || h.scope}`}
                      className={`flex items-center gap-4 p-4 ${isPast ? 'opacity-50' : ''}`}
                      data-testid={`holiday-${h.date}${isLocal ? '-' + (h.town || '').replace(/\s+/g, '-') : ''}`}
                    >
                      <div className={`w-1.5 self-stretch rounded-full ${s.dot}`} aria-hidden />
                      <div className="w-12 text-center shrink-0">
                        <p className="text-2xl font-bold text-gray-900 tabular-nums">{h.dateObj.getDate()}</p>
                        <p className="text-[10px] uppercase tracking-wide text-gray-400">
                          {h.dateObj.toLocaleDateString(locale, { weekday: 'short' })}
                        </p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900">{holidayName(h)}</p>
                        {isLocal && h.town && (
                          <p className="text-xs font-semibold text-red-600 mt-0.5 flex items-center gap-1" data-testid={`holiday-town-${h.date}`}>
                            <MapPin className="h-3 w-3" /> {h.town} — {t('holidays.localHoliday', 'Local Holiday')}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 mt-0.5">{formatDate(h.dateObj)}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-[11px] font-medium ring-1 whitespace-nowrap ${s.cls}`}>
                        {t(s.key, s.fallback)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <p className="text-xs text-gray-400 text-center mt-8">
          {t('holidays.footerNote', 'Local holidays are municipality-specific. Verify with your local council for certainty.')}
        </p>
      </main>

      <BottomNavigation />
    </div>
  );
};

export default Holidays;
